/**
 * Renders language-neutral music tokens ("note:F#4", "interval:m3") as terms in the
 * reader's language. All wording comes from the glossary served by the API, so
 * content authors own the terminology and the client holds none of it.
 */
import type { Glossary } from '@/shared/api/types';

import { createSegmenter, listTerms, type Segmenter, type TermRef } from '../text/segments';

/** Letters (C D E) or syllables (do re mi). Independent of the interface language. */
export type NoteNaming = 'letters' | 'solfege';

export interface MusicFormatter {
  /** "F#4" or "F#" as a note name. */
  note(pitch: string): string;
  /** A token such as "interval:m3". Unknown tokens are returned unchanged. */
  token(token: string): string;
  /** The abbreviation of a term ("m3"), when the language has one. */
  short(token: string): string | undefined;
  /** Replace every {{kind:value}} in a text. */
  text(text: string): string;
  /** A text split into plain stretches, note names and glossary terms. */
  segments: Segmenter;
  /** The glossary entry a token such as "term:sharp" names. */
  term(token: string): TermRef | undefined;
  /** Every entry of the glossary. */
  terms(): TermRef[];
}

const NOTE_RE = /^([A-G])(##|bb|#|b)?(\d)?$/;
const EMBEDDED_RE = /\{\{\s*([a-z]+:[^}\s]+)\s*\}\}/g;

function fill(pattern: string, values: Record<string, string>): string {
  return pattern.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole);
}

function formatNote(pitch: string, glossary: Glossary, naming: NoteNaming): string {
  const match = NOTE_RE.exec(pitch);
  const system = glossary.notes[naming] ?? glossary.notes[glossary.defaultNaming];
  if (!match || !system) return pitch;
  const [, letter = '', accidental, octave] = match;

  let name = system.names[letter] ?? letter;
  if (accidental) {
    name = fill(system.pattern, { note: name, acc: system.accidentals[accidental] ?? accidental });
  }
  if (octave === undefined) return name;
  return fill(glossary.octavePattern, {
    note: name,
    number: octave,
    octave: glossary.octaves[octave] ?? octave,
  });
}

function findTerm(token: string, glossary: Glossary) {
  const separator = token.indexOf(':');
  const kind = token.slice(0, separator);
  const value = token.slice(separator + 1);
  const sections = {
    interval: glossary.intervals,
    chord: glossary.chords,
    scale: glossary.scales,
    duration: glossary.durations,
    term: glossary.terms,
  } as const;
  return { kind, value, term: sections[kind as keyof typeof sections]?.[value] };
}

/** A formatter for one language and naming system. Without a glossary it echoes tokens. */
export function createMusicFormatter(
  glossary: Glossary | undefined,
  naming: NoteNaming | null,
): MusicFormatter {
  if (!glossary) {
    const raw = (token: string) => token.slice(token.indexOf(':') + 1);
    return {
      note: (pitch) => pitch,
      token: raw,
      short: () => undefined,
      text: (text) => text.replace(EMBEDDED_RE, (_, token: string) => raw(token)),
      segments: createSegmenter(undefined, { note: (pitch) => pitch }),
      term: () => undefined,
      terms: () => [],
    };
  }

  const system = naming ?? glossary.defaultNaming;
  const terms = listTerms(glossary);
  const byKey = new Map(terms.map((term) => [term.key, term]));
  const formatToken = (token: string): string => {
    const { kind, value, term } = findTerm(token, glossary);
    if (kind === 'note') return formatNote(value, glossary, system);
    return term?.name ?? value;
  };
  return {
    note: (pitch) => formatNote(pitch, glossary, system),
    token: formatToken,
    short: (token) => findTerm(token, glossary).term?.short ?? undefined,
    text: (text) => text.replace(EMBEDDED_RE, (_, token: string) => formatToken(token)),
    segments: createSegmenter(glossary, { note: (pitch) => formatNote(pitch, glossary, system) }),
    term: (token) => byKey.get(token),
    terms: () => terms,
  };
}
